import type { AIModelChoices, Annotation } from 'api';
import { useCallback, useMemo, useState } from 'react';

import { getIgnoredLabels } from '../../constants';
import { getUniqueSegmentationCategories } from '../../utils/formatAnnotations/formatAnnotations';

interface UseAnnotationsReturn {
  selectedAnnotation: Annotation[] | undefined;
  handleImageChange: (annotations: Annotation[] | undefined) => void;
  hiddenCategories: string[];
  handleHideCategory: (label: string) => void;
  handleToggleLegend: () => void;
  showLegend: boolean;
  showAIFilters: boolean;
}

export const useAnnotations = ({
  initialAnnotations,
  model,
}: {
  initialAnnotations?: Annotation[];
  model?: AIModelChoices;
}): UseAnnotationsReturn => {
  const [selectedAnnotation, setSelectedAnnotation] = useState<Annotation[] | undefined>(initialAnnotations);
  const [hiddenCategories, setHiddenCategories] = useState<string[]>([]);
  const [showLegend, setShowLegend] = useState<boolean>(true);

  const allSegmentationCategories = useMemo(
    () => getUniqueSegmentationCategories(selectedAnnotation || [], model),
    [selectedAnnotation, model]
  );

  const handleHideCategory = useCallback((label: string) => {
    setHiddenCategories((prev) =>
      prev.includes(label) ? prev.filter((category) => category !== label) : [...prev, label]
    );
  }, []);

  const hideAllCategories = useCallback(
    (annotations?: Annotation[]) => {
      const categories = annotations ? getUniqueSegmentationCategories(annotations) : allSegmentationCategories;

      setHiddenCategories(Array.from(categories).map((category) => category[0]));
    },
    [allSegmentationCategories]
  );

  const showAllCategories = useCallback(() => {
    setHiddenCategories([]);
  }, []);

  const handleToggleLegend = useCallback(() => {
    setShowLegend((prev) => {
      const newShowLegend = !prev;
      if (!newShowLegend) {
        hideAllCategories();
      } else {
        showAllCategories();
      }
      return newShowLegend;
    });
  }, [hideAllCategories, showAllCategories]);

  const handleImageChange = useCallback(
    (annotations: Annotation[] | undefined) => {
      setSelectedAnnotation(annotations);
      if (!showLegend && annotations) {
        hideAllCategories(annotations);
      }
    },
    [showLegend, hideAllCategories]
  );

  const showAIFilters = useMemo(() => {
    if (!selectedAnnotation?.length) return false;
    const ignoredLabels = model ? getIgnoredLabels(model) : [];
    return !selectedAnnotation.every((annotation) => ignoredLabels.includes(annotation.label));
  }, [selectedAnnotation, model]);

  return {
    handleHideCategory,
    handleImageChange,
    handleToggleLegend,
    hiddenCategories,
    selectedAnnotation,
    showAIFilters,
    showLegend,
  };
};
